import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { AppStore } from "../../../redux/store";
import { get } from "lodash";
import { GadmFeature } from "../components/Map/types";
import { HeatMapPolygon, HeatMapV2Data } from "../models/heatMapModel";
import { v2PolygonsEndpoint } from "../services/heatMapService";
import { fetchResponseAdapter } from "../../../adapters/fetchAdapter";
import useCallApiAndLoad from "../../../hooks/useCallApiAndLoad";

const toFeature = (polygon: HeatMapPolygon): GadmFeature => ({
  type: "Feature",
  geometry: {
    type: polygon.geo_points.type as "Polygon" | "MultiPolygon",
    coordinates: polygon.geo_points.coordinates,
  },
  properties: {
    GID_0: "",
    [`NAME_${polygon.level}`]: polygon.name,
    id: polygon.id,
  },
});

interface PolygonsState {
  features: GadmFeature[];
  level: number;
  parentId: number | null;
  loading: boolean;
  data: HeatMapV2Data | null;
  trail: Array<{ level: number; id: number; name: string }>;
}

const usePolygons = () => {
  const country = useSelector((store: AppStore) => get(store, "country.country", "sv"));
  const [searchParams, setSearchParams] = useSearchParams();
  const skipUrlSync = useRef(false);
  const isFirstRender = useRef(true);

  const { callEndpoint } = useCallApiAndLoad();
  const callRef = useRef(callEndpoint);
  callRef.current = callEndpoint;

  const [priceType, setPriceType] = useState("sale");
  const [priceMin, setPriceMin] = useState<number | undefined>();
  const [priceMax, setPriceMax] = useState<number | undefined>();

  const [state, setState] = useState<PolygonsState>({
    features: [],
    level: 1,
    parentId: null,
    loading: false,
    data: null,
    trail: [],
  });

  const fetchPolygons = useCallback(async (params: { level?: number; parent?: number }): Promise<HeatMapV2Data | null> => {
    // TODO: reactivar min_price y max_price cuando se integre el filtro por precio
    const response = await callRef.current(v2PolygonsEndpoint({ ...params, price_type: priceType }));
    const adapted = fetchResponseAdapter(response?.data);
    return adapted?.code === 0 ? adapted.data : null;
  }, [priceType]);

  const loadByLevel = useCallback(async (level: number) => {
    setState(prev => ({ ...prev, loading: true }));
    const data = await fetchPolygons({ level });
    if (data) {
      setState(prev => ({
        ...prev,
        features: data.polygons.map(toFeature),
        level,
        parentId: null,
        loading: false,
        data,
        trail: prev.trail.filter(item => item.level < level),
      }));
    } else {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [fetchPolygons]);

  const restoreFromTrail = useCallback(async (trailIds: string[]) => {
    skipUrlSync.current = true;
    setState(prev => ({ ...prev, loading: true }));
    try {
      const trail: PolygonsState["trail"] = [];
      for (let i = 0; i < trailIds.length; i++) {
        const id = Number(trailIds[i]);
        const params = i === 0 ? { level: 1 } : { level: i + 1, parent: trail[i - 1].id };
        const data = await fetchPolygons(params);
        const polygon = data?.polygons.find(p => p.id === id);
        trail.push({ level: i + 1, id, name: polygon?.name ?? String(id) });
      }
      const lastId = trail[trail.length - 1].id;
      const finalData = await fetchPolygons({ level: trailIds.length + 1, parent: lastId });
      if (finalData) {
        setState({
          features: finalData.polygons.map(toFeature),
          level: trailIds.length + 1,
          parentId: lastId,
          loading: false,
          data: finalData,
          trail,
        });
      }
    } catch {
      setState(prev => ({ ...prev, loading: false }));
      loadByLevel(1);
    } finally {
      skipUrlSync.current = false;
    }
  }, [fetchPolygons, loadByLevel]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const trailParam = searchParams.get("trail");
      if (trailParam) {
        restoreFromTrail(trailParam.split(","));
        return;
      }
    }
    loadByLevel(1);
  }, [country]);

  // Inicializa precios con los valores de la API al primer fetch
  useEffect(() => {
    if (!state.data) return;
    if (priceMin === undefined && state.data.min_price_range_value) setPriceMin(state.data.min_price_range_value);
    if (priceMax === undefined && state.data.max_price_range_value) setPriceMax(state.data.max_price_range_value);
  }, [state.data]);

  useEffect(() => {
    if (skipUrlSync.current) return;
    const trailIds = state.trail.map(t => String(t.id)).join(",");
    setSearchParams(trailIds ? { trail: trailIds } : {}, { replace: true });
  }, [state.trail]);

  const loadByParent = useCallback(async (parentId: number, level: number, parentName?: string) => {
    setState(prev => ({ ...prev, loading: true }));
    const data = await fetchPolygons({ level, parent: parentId });
    if (data) {
      setState(prev => {
        const baseTrail = prev.trail.filter(item => item.level < level - 1);
        const trail = parentName
          ? [...baseTrail, { level: level - 1, id: parentId, name: parentName }]
          : baseTrail;
        return { ...prev, features: data.polygons.map(toFeature), level, parentId, loading: false, data, trail };
      });
    } else {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [fetchPolygons]);

  const navigateToLevel = useCallback(async (targetLevel: number) => {
    if (targetLevel <= 1) {
      await loadByLevel(1);
      return;
    }
    const parentNode = state.trail.find(item => item.level === targetLevel - 1);
    if (!parentNode) {
      await loadByLevel(1);
      return;
    }
    setState(prev => ({ ...prev, loading: true }));
    const data = await fetchPolygons({ level: targetLevel, parent: parentNode.id });
    if (data) {
      setState(prev => ({
        ...prev,
        features: data.polygons.map(toFeature),
        level: targetLevel,
        parentId: parentNode.id,
        loading: false,
        data,
        trail: prev.trail.filter(item => item.level < targetLevel),
      }));
    } else {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [fetchPolygons, loadByLevel, state.trail]);

  return {
    features: state.features,
    level: state.level,
    parentId: state.parentId,
    loading: state.loading,
    data: state.data,
    trail: state.trail,
    priceType,
    setPriceType,
    priceMin,
    priceMax,
    setPriceMin,
    setPriceMax,
    loadByLevel,
    loadByParent,
    navigateToLevel,
  };
};

export default usePolygons;
